import { useAuth0 } from '@auth0/auth0-react';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { ReasonInput } from '@strata/react-compound-inputs';
import { DateInputFormik } from '@strata/react-date-time';
import { FormContainer } from '@strata/react-form-container';
import { FormHeaderAndContent } from '@strata/react-form-header-and-content';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { InlineMessage } from '@strata/react-inline-message';
import { TextAreaFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { ErrorToast, SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { unmanageTreatment } from 'api/treatmentAPI';
import { handleError } from 'services/Error';
import { useGetPayersQuery } from 'store/api/referenceApi';

const ReasonOptions = [
    'Secondary coverage',
    'Coverage terminated',
    'Out of network pharmacy',
    'Buy-and-bill',
    'Out of area patient',
    'Benefit coverage changed',
];

export const BulkUnmanage = () => {
    const { user } = useAuth0();
    const { data, isFetching } = useGetPayersQuery();
    const { payerList } = data ?? {};

    return (
        <FormHeaderAndContent
            mainContent={
                <Formik
                    initialValues={{}}
                    validationSchema={Yup.object({
                        payerId: Yup.string().required(
                            validationStrings.requiredField.errorMessage,
                        ),
                        treatmentIds: Yup.string()
                            .required(validationStrings.requiredField.errorMessage)
                            .matches(/^.+$/gm, 'IDs formatted incorrectly.'),
                        transitionDate: DateInputFormik.validator().required(
                            validationStrings.requiredField.errorMessage,
                        ),
                        ...ReasonInput.validationSchema,
                    })}
                    onSubmit={async (values, { resetForm }) => {
                        const { payerId, treatmentIds, transitionDate } = values;
                        const formattedDate = formatDate(transitionDate, DateFormats.HYPHEN_DATE);
                        try {
                            const errors = (
                                await Promise.all(
                                    treatmentIds.match(/^.+$/gm).map((treatmentId) =>
                                        (async () => {
                                            try {
                                                await unmanageTreatment({
                                                    payerId,
                                                    treatmentId: treatmentId.trim(),
                                                    effectiveDate: formattedDate,
                                                    username: user?.name,
                                                    ...ReasonInput.submitValues(values),
                                                });
                                                return undefined;
                                            } catch {
                                                return treatmentId;
                                            }
                                        })(),
                                    ),
                                )
                            ).filter((err) => err);

                            if (errors.length > 0) {
                                ErrorToast({
                                    message: (
                                        <div>
                                            <div>
                                                The following treatments could not be unmanaged:
                                            </div>
                                            {errors.map((id) => (
                                                <div key={id}>{id}</div>
                                            ))}
                                        </div>
                                    ),
                                    timeout: 0,
                                });
                            } else {
                                SuccessToast({ message: 'Unmanaged treatments successfully.' });
                                resetForm();
                            }
                        } catch (err) {
                            handleError(
                                'There was an error attempting this action, please try again.',
                                err,
                            );
                        }
                    }}
                >
                    {(formik) => (
                        <FormContainer
                            formTitle="Bulk Unmanage"
                            primaryButtonLabel="Unmanage Treatments"
                            onFormSubmit={formik.handleSubmit}
                            onSecondaryButtonClick={formik.handleReset}
                            isSubmitting={formik.isSubmitting || !(formik.isValid && formik.dirty)}
                            formLeftContent={
                                <>
                                    <FullscreenLoading
                                        isShowing={formik.isSubmitting}
                                        loadingText="Unmanaging treatments..."
                                    />
                                    <div className="margin__bottom--medium">
                                        <InlineMessage
                                            intent={InlineMessage.Intent.INFO}
                                            wrapText
                                            label="This action will update the treatment status to unmanaged and update the referral status of the active referral under each treatment in the pharmacy's view to EXPIRED. Completed performance requirements will be evaluated at the end of the evaluation period, in progress performance will be VOIDED if the minimum days have not been met, and related tasks will be removed."
                                        />
                                    </div>
                                    <SuggestSingleSelectFormik
                                        id="payerId"
                                        label="Health Plan"
                                        required
                                        isLoading={isFetching}
                                        values={payerList}
                                        selectedItem={payerList?.find(
                                            ({ value }) => value === formik.values.payerId,
                                        )}
                                        onChange={(newValue) =>
                                            formik.setFieldValue('payerId', newValue?.value)
                                        }
                                    />
                                    <TextAreaFormik
                                        label="Treatment IDs"
                                        id="treatmentIds"
                                        helperText="One ID per line."
                                        required
                                    />
                                    <DateInputFormik
                                        label="Date To Unmanage"
                                        id="transitionDate"
                                        required
                                    />
                                    <ReasonInput reasons={ReasonOptions} />
                                </>
                            }
                        />
                    )}
                </Formik>
            }
        />
    );
};
